<template>
  <Container
    isFlex
    isAlignCenter
    isJustifyCenter
    :isFillHeight="isSpinner || intervalMethod.isError"
  >
    <div v-if="isShowHeader && !intervalMethod.isError">
      <Header
        :items="displayItems"
        :attachments="preview.detail.emailAttachments"
        class="is-wvw-90"
      />
      <Divider />
    </div>
    <Body v-if="preview.body && !intervalMethod.isError" :body="preview.body" />
    <Spinner v-if="isSpinner" style="margin: auto" :message="message" />
    <div style="text-align: center" v-if="intervalMethod.isError">
      <p>{{ message }}</p>
      <Button primary @click="retryPetition()"> Reintentar </Button>
    </div>
  </Container>
</template>

<script>
import { mapGetters } from 'vuex';
import { typesApp } from '@/store/app/typesModuleApp';
import PreviewApi from '@/api/previewApi';

const previewApi = new PreviewApi();
const waitTimeOut = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default {
  name: 'EmailPreview',

  components: {
    Container: () => import('@/components/Main/Container.vue'),
    Header: () => import('@/components/Preview/Header.vue'),
    Body: () => import('@/components/Preview/Body.vue'),
    Spinner: () => import('@/components/Util/Spinner.vue'),
    Divider: () => import('@/components/Util/Divider.vue'),
    Button: () => import('@/components/Util/Button.vue'),
  },

  data() {
    return {
      preview: {
        detail: null,
        body: null,
      },
      intervalMethod: {
        id: null,
        timer: 0,
        seconds: 30,
        retry: 0,
        maxRetry: 0,
        isError: false,
        miliseconds: 60000,
      },
      message: 'Estamos consultando la información...',
      itemsAvailable: [
        {
          name: 'emailSubject',
          label: 'Asunto:',
          maxSize: 100,
        },
        {
          name: 'emailSentDate',
          label: 'Fecha:',
          formatterDate: 'LLLL',
        },
        {
          name: 'emailFrom',
          label: 'De:',
        },
        {
          name: 'emailTo',
          label: 'Para:',
        },
        {
          name: 'lastEventStatus',
          label: 'Último Evento:',
        },
        {
          name: 'lastEventDate',
          label: 'Fecha último evento:',
          formatterDate: 'LLLL',
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      getRecaptchaId: `${typesApp.PATH}/${typesApp.getters.GET_RECAPTCHA_ID}`,
    }),

    deliveryId() {
      return this.$route.query.deliveryId;
    },
    customerId() {
      return this.$route.query.customerId;
    },
    sourceId() {
      return this.$route.query.sourceId;
    },
    emailId() {
      return this.$route.query.emailId;
    },
    showHeader() {
      return this.$route.query.sh === 'true';
    },
    isShowHeader() {
      return this.showHeader && this.preview.detail;
    },
    displayItems() {
      const items = [];

      if (this.preview.detail) {
        this.itemsAvailable.forEach(({ name, label, maxSize, formatterDate }) => {
          let value = this.preview.detail[name] || null;
          if (formatterDate && value) value = this.moment(value).format(formatterDate);
          items.push({ label, value, maxSize });
        });
      }

      return items;
    },
    isSpinner() {
      return (!this.preview.detail || !this.preview.body) && !this.intervalMethod.isError;
    },
  },

  watch: {
    'intervalMethod.timer': {
      async handler(val) {
        if (val >= this.intervalMethod.seconds) {
          this.intervalMethod.retry = 0;
          await this.getPreviewDetail(true);
        }
      },
      deep: true,
    },
  },

  methods: {
    async getPreviewDetail(onlyLastEvent = false, isLambda = false) {
      try {
        this.intervalMethod.timer = 0;
        clearInterval(this.intervalMethod.id);

        const payload = {
          deliveryId: this.deliveryId,
          customerId: this.customerId,
          sourceId: this.sourceId,
          emailId: this.emailId,
          onlyLastEvent,
          recaptchaId: this.getRecaptchaId,
        };

        const { data } = await previewApi.getPreviewDetail(payload, true, isLambda);

        this.preview.detail = data;
        if (this.preview.detail && !this.preview.detail.lastEventStatus) {
          // Replicate the query each 30 seconds
          this.intervalMethod.id = setInterval(() => {
            this.intervalMethod.timer += 1;
          }, 1000);
        }
      } catch (error) {
        const { status } = error.response;
        const isRetry = ![500].includes(status);

        if (isRetry && this.intervalMethod.retry <= this.intervalMethod.maxRetry) {
          if (!this.preview.detail) {
            this.message =
              'Esta busqueda nos puede llevar más tiempo de lo habitual, por favor espere...';
          }
          const isCustomLambda = this.intervalMethod.retry === this.intervalMethod.maxRetry;
          this.intervalMethod.retry += 1;
          await waitTimeOut(this.intervalMethod.miliseconds);
          await this.getPreviewDetail(false, isCustomLambda);
        } else if (!this.preview.detail) {
          this.message = `Lo sentimos! no pudimos obtener la información en este momento, por favor intente más tarde o comuníquese con soporte. Codigo (${
            status || 504
          })`;
          this.intervalMethod.isError = true;
        }
      }
    },
    async getPreviewBody() {
      const payload = this.preview.detail;

      try {
        this.message = 'Estamos consultando la información...';

        const { data } = await previewApi.getPreviewBody(payload, true);

        this.preview.body = data;
      } catch (error) {
        this.message = 'Estamos intentando recuperar el contenido del correo, por favor espere...';
        await waitTimeOut(this.intervalMethod.miliseconds);
        await this.getPreviewBody(payload, true);
      }
    },

    async retryPetition() {
      this.intervalMethod.retry = 0;
      this.intervalMethod.timer = 0;
      this.intervalMethod.isError = false;
      this.message = 'Estamos consultando la información...';
      await this.getPreviewDetail();
    },
  },

  async mounted() {
    await this.getPreviewDetail();
    if (this.preview.detail) {
      await this.getPreviewBody();
    }
  },
};
</script>
